import React, { useCallback, useEffect, useRef, useState } from 'react';

import Tooltip from 'CommonComponents/Tooltip';

const CHIP_CLASSES = 'tw-bg-charcoal-15 tw-text-xs tw-text-nowrap tw-font-medium tw-px-1.5 tw-py-1 tw-rounded-lg';

// Click is handled here rather than by the hook, which toggles on click: a
// click that lands after hover has opened the popover would close it again.
const POPOVER_TRIGGER = ['hover'];

/**
 * A selection shown as chips that wrap inside a fixed-width cell: "All" when
 * the selection covers everything, otherwise the first few names and a
 * "+N more" chip whose popover lists every one of them.
 */
export default function ScopeChips({ items = [], all = false, maxVisible = 3 }) {
    if (all) {
        return (
            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                <div className={CHIP_CLASSES}>All</div>
            </div>
        );
    }

    if (!items.length) {
        return null;
    }

    const visibleItems = items.slice(0, maxVisible);
    const hiddenCount = items.length - visibleItems.length;

    // dir="auto" lays each name out in its own script's direction, so on an RTL
    // page an English name keeps its word order and is cut off at its end.
    return (
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {visibleItems.map((item, index) => (
                <div key={`${index}-${item}`} dir="auto" className={`${CHIP_CLASSES} tw-truncate tw-max-w-[7rem]`} title={item}>
                    {item}
                </div>
            ))}
            {hiddenCount > 0 && <MoreChip items={items} hiddenCount={hiddenCount} />}
        </div>
    );
}

// React 16 has no useId; a module counter gives each popover a stable id.
let popoverCount = 0;

const isRtl = () => typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

// Keys that move focus from the "+N more" button into its open list. The list
// is portalled to the end of the body, so Tab alone would never reach it.
const ENTER_LIST_KEYS = ['ArrowDown', 'Enter', ' '];

function MoreChip({ items, hiddenCount }) {
    const [open, setOpen] = useState(false);
    const listRef = useRef(null);
    const buttonRef = useRef(null);
    const listId = useRef(null);
    // Set while the list hands focus back to the button, so that focus does
    // not reopen the list it just closed.
    const returningFocus = useRef(false);

    if (listId.current === null) {
        popoverCount += 1;
        listId.current = `scope-chips-popover-${popoverCount}`;
    }

    useEffect(() => {
        if (!open) {
            return;
        }

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('keydown', closeOnEscape);

        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [open]);

    // Focus moving into the list (by keyboard, or by grabbing its scrollbar)
    // keeps it open.
    const closeOnBlur = useCallback((event) => {
        if (listRef.current && listRef.current.contains(event.relatedTarget)) {
            return;
        }

        setOpen(false);
    }, []);

    const handleButtonFocus = () => {
        if (returningFocus.current) {
            returningFocus.current = false;
            return;
        }

        setOpen(true);
    };

    const handleButtonKeyDown = (event) => {
        if (open && ENTER_LIST_KEYS.includes(event.key)) {
            event.preventDefault();
            listRef.current?.focus();
        }
    };

    // The flag is only raised when the focus call will actually fire a focus
    // event to clear it.
    const returnFocusToButton = () => {
        const button = buttonRef.current;

        setOpen(false);

        if (!button || document.activeElement === button) {
            return;
        }

        returningFocus.current = true;
        button.focus();
    };

    const handleListKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            returnFocusToButton();
        }

        // The list sits at the end of the body; Tab is let through from the
        // button so it reaches the controls beside it in the row.
        if (event.key === 'Tab') {
            returnFocusToButton();
        }
    };

    const label = `+${hiddenCount} more, show all ${items.length} selected`;

    return (
        <Tooltip
            tooltipType="content"
            placement={isRtl() ? 'bottom-end' : 'bottom-start'}
            trigger={POPOVER_TRIGGER}
            interactive
            delayHide={150}
            visible={open}
            onVisibleChange={setOpen}
            triggerRef={buttonRef}
            tooltipRole="presentation"
            backgroundClass="tw-bg-white tw-border tw-border-grey !tw-opacity-100"
            tooltipContent={
                <div
                    ref={listRef}
                    id={listId.current}
                    role="dialog"
                    aria-label="Selected items"
                    tabIndex={-1}
                    onBlur={closeOnBlur}
                    onKeyDown={handleListKeyDown}
                    className="tw-max-h-64 tw-max-w-xs tw-overflow-y-auto tw-pe-2 tw-text-xs tw-text-charcoal"
                >
                    <ul className="tw-flex tw-flex-col tw-gap-y-1">
                        {items.map((item, index) => (
                            <li key={`${index}-${item}`} dir="auto" className="tw-break-words">
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            }
        >
            <button
                type="button"
                dir="ltr"
                className={`${CHIP_CLASSES} tw-text-charcoal-75 hover:tw-bg-charcoal-25`}
                aria-label={label}
                aria-haspopup="dialog"
                aria-controls={open ? listId.current : undefined}
                aria-expanded={open}
                onClick={() => setOpen(true)}
                onFocus={handleButtonFocus}
                onBlur={closeOnBlur}
                onKeyDown={handleButtonKeyDown}
            >
                +{hiddenCount} more
            </button>
        </Tooltip>
    );
}
